import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { ApplyButton } from "@/components/Buttons";
import { PaperPlane } from "@/components/PaperPlane";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    axios.get(`${API}/blog`).then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center min-h-[60vh]">
        <h1 className="font-display font-black text-4xl text-white">Article not found</h1>
        <Link to="/blog" className="inline-block mt-6 text-gold link-underline">← Back to all articles</Link>
      </div>
    );
  }
  if (!post) return <div className="pt-40 pb-32 text-center text-cream/50 min-h-[60vh]">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: "Prolific Maven" },
    datePublished: post.published_at,
    mainEntityOfPage: `https://prolificmaven.com/blog/${post.slug}`,
  };

  return (
    <>
      <Seo title={`${post.title} | Prolific Maven`} description={post.excerpt} path={`/blog/${post.slug}`} jsonLd={jsonLd} />

      <article className="relative pt-32 pb-24" data-testid="blog-article">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link to="/blog" className="text-gold text-sm link-underline" data-testid="back-to-blog">← All articles</Link>
          <div className="grid lg:grid-cols-12 gap-12 mt-8">
            <div className="lg:col-span-8">
              <span className="text-gold text-xs uppercase tracking-widest">{post.category}</span>
              <h1 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.08] tracking-tight">{post.title}</h1>
              <p className="mt-5 text-cream/40 text-sm uppercase tracking-widest">By {post.author} · {post.published_at} · {post.read_time}</p>
              <img src={post.cover_image} alt={`${post.title}, Prolific Maven`} className="mt-8 w-full border border-white/10 aspect-[16/9] object-cover" />
              <div className="prose-pm mt-12 max-w-none text-lg" dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>

            <aside className="lg:col-span-4 space-y-8">
              <div className="bg-ink-800 border border-gold/30 p-8 lg:sticky lg:top-28">
                <PaperPlane className="w-8 h-8 mb-4" />
                <h3 className="font-display font-bold text-xl text-white">Ready to amplify your voice?</h3>
                <p className="mt-3 text-cream/60 text-sm leading-relaxed">Apply to work with us. We only take on a limited number of local clients at a time.</p>
                <div className="mt-6"><ApplyButton testid="blog-sidebar-apply" className="w-full text-sm px-6 py-3" /></div>
              </div>

              <div className="bg-ink-800 border border-white/10 p-8">
                <h3 className="text-xs uppercase tracking-widest text-gold mb-2">Author</h3>
                <p className="text-white font-display font-semibold">{post.author}</p>
                <p className="mt-2 text-cream/60 text-sm leading-relaxed">Founder of Prolific Maven, based in Parrish, FL, helping local businesses clarify, refine, and amplify their voice across the Tampa Bay area.</p>
              </div>

              {related.length > 0 && (
                <div className="bg-ink-800 border border-white/10 p-8">
                  <h3 className="text-xs uppercase tracking-widest text-gold mb-4">Related posts</h3>
                  <ul className="space-y-4">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link to={`/blog/${r.slug}`} className="text-cream/80 hover:text-gold text-sm leading-snug transition-colors block">{r.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}
